/**
 * BookingsPage classification + filtering helpers (pure, unit-tested). The server pre-filters each tab
 * by lastTransitions, but "upcoming vs past" also depends on the booking date (an accepted booking
 * whose session already ended is Past), so the final section decision is made here.
 */

import { isCompleted, isRefunded } from '../../transactions/transactionProcessBooking';
import { getBookedChild } from '../../util/bookingData';
import { isBeforeRescheduleCutoff } from '../../util/refundCalculation';
import { isTermFormat } from '../../util/createClass';
import { getOneTimeSessions, isOneTimeDropIn } from '../../util/oneTimeSchedule';
import { getTermSessions } from '../../util/termSchedule';

// Child colors live in util/childData (used app-wide, incl. the transaction page); re-exported here so
// the bookings modules keep a single import surface.
export {
  CHILD_MARKER_COLORS,
  buildChildColorMap,
  childColorFor,
  isRemovedChildName,
} from '../../util/childData';

/** The booking's end instant (displayEnd falls back to end), or null. */
export const bookingEndDate = tx => {
  const b = tx?.booking?.attributes;
  const end = b?.displayEnd || b?.end || null;
  return end ? new Date(end) : null;
};

/** The booking's start instant (displayStart falls back to start), or null. */
export const bookingStartDate = tx => {
  const b = tx?.booking?.attributes;
  const start = b?.displayStart || b?.start || null;
  return start ? new Date(start) : null;
};

/**
 * Classify a booking transaction into a section.
 * - cancelled: a terminal "not happening" transition (refunded: cancel/decline/expire/…).
 * - past: a completed transition (delivered/reviewed), OR still accepted but the session has ended.
 * - upcoming: otherwise (accepted / reschedule-proposed with the session still ahead).
 * @param {Object} tx transaction entity
 * @param {Date} [now]
 * @returns {'upcoming'|'past'|'cancelled'}
 */
export const classifyBooking = (tx, now = new Date()) => {
  const lastTransition = tx?.attributes?.lastTransition;
  if (isRefunded(lastTransition)) {
    return 'cancelled';
  }
  if (isCompleted(lastTransition)) {
    return 'past';
  }
  const end = bookingEndDate(tx);
  if (end && end.getTime() < now.getTime()) {
    return 'past';
  }
  return 'upcoming';
};

/** The booked child's first name for this booking (from the audited snapshot), or null. */
export const bookedChildFirstName = tx => {
  const snapshot = getBookedChild(tx?.attributes?.protectedData);
  const name = snapshot?.firstName;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
};

/**
 * The calendar day a booking falls on, as a YYYY-MM-DD key in the LISTING's timezone (so a 7pm PT slot
 * lands on that day's cell, not the next). Used to place calendar dots + filter the list by day.
 */
const dayKeyInListingZone = (date, tx) => {
  const timeZone = tx?.listing?.attributes?.availabilityPlan?.timezone;
  try {
    return new Intl.DateTimeFormat('en-CA', timeZone ? { timeZone } : {}).format(date);
  } catch (e) {
    return new Intl.DateTimeFormat('en-CA').format(date);
  }
};

export const bookingDayKey = tx => {
  const start = bookingStartDate(tx);
  return start ? dayKeyInListingZone(start, tx) : null;
};

/**
 * Every session occurrence of a booking as { start, end }. A single-session booking is its own one
 * occurrence (the booking window). A term-enrollment booking is the WHOLE series from its listing's
 * termSchedule -- surfaces that used the booking start alone showed only the first session of a
 * multi-week class. Falls back to the booking window when the listing has no usable schedule.
 */
export const bookingOccurrences = tx => {
  if (isTermFormat(tx?.listing?.attributes?.publicData?.classFormat)) {
    const sessions = getTermSessions(tx?.listing);
    if (sessions.length > 0) {
      return sessions;
    }
  }
  const start = bookingStartDate(tx);
  return start ? [{ start, end: bookingEndDate(tx) }] : [];
};

/**
 * The calendar day keys a booking occupies (one per occurrence; multi-entry for term bookings).
 * Use this for calendar dots and day filtering so every session of a term shows, not just the first.
 */
export const bookingDayKeys = tx =>
  bookingOccurrences(tx).map(occurrence => dayKeyInListingZone(occurrence.start, tx));

/**
 * The bookings for a given tab + child selection, newest session first.
 * @param {Array} transactions
 * @param {string} tab 'upcoming' | 'past' | 'cancelled'
 * @param {string} childFilter 'all' or a child's first name
 * @param {Date} [now]
 */
export const bookingsForView = (transactions, tab, childFilter = 'all', now = new Date()) => {
  const inTab = (transactions || []).filter(tx => classifyBooking(tx, now) === tab);
  const filtered =
    childFilter && childFilter !== 'all'
      ? inTab.filter(tx => bookedChildFirstName(tx) === childFilter)
      : inTab;

  // Upcoming: soonest first. Past/Cancelled: most recent first.
  const dir = tab === 'upcoming' ? 1 : -1;
  return filtered.slice().sort((a, b) => {
    const ta = bookingStartDate(a)?.getTime() ?? 0;
    const tb = bookingStartDate(b)?.getTime() ?? 0;
    return (ta - tb) * dir;
  });
};

/**
 * Whether a parent may reschedule this booking: single-session only (term reschedule is out of scope),
 * for a drop-in with set dates only when it has another upcoming session,
 * AND at least RESCHEDULE_CUTOFF_HOURS before the session (client decision: 12h -- the old
 * free-cancel-window gate hid the button entirely for strict policies). Mirrors the server gate;
 * the server remains authoritative.
 * @param {Object} tx
 * @param {Date} [now]
 */
export const canRescheduleBooking = (tx, now = new Date()) => {
  const publicData = tx?.listing?.attributes?.publicData;
  if (publicData?.classFormat !== 'single-session') {
    return false;
  }
  // A drop-in with set dates: only when the class has ANOTHER upcoming session to move into.
  if (isOneTimeDropIn(publicData)) {
    const bookedMs = new Date(tx?.booking?.attributes?.start).getTime();
    const hasOther = getOneTimeSessions(tx.listing).some(
      s => s.start.getTime() !== bookedMs && s.start.getTime() > now.getTime()
    );
    if (!hasOther) {
      return false;
    }
  }
  return isBeforeRescheduleCutoff(tx?.booking?.attributes?.start, now);
};

/** The distinct booked-child first names present across the given transactions (for the filter chips). */
export const bookedChildNames = transactions => {
  const names = new Set();
  (transactions || []).forEach(tx => {
    const name = bookedChildFirstName(tx);
    if (name) {
      names.add(name);
    }
  });
  return Array.from(names);
};
